/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { debounce } from 'lodash';

import UiContext from 'common/UiContext/UiContext';
import useApi from 'common/useApi';

import IFilters from './IFilter';

import Icons from 'assets/images/Icons';
import EditWorkers from 'components/EditWorkers';
import FlagForRepair from 'components/FlagForRepair';
import SelectInput from 'components/SelectInput';

interface IFiltersProps {
  refetch(): Promise<UseQueryResult>
  checkedIds: Array<number>
  setCheckedIds: (ids: Array<number>) => void
  setFilters: Dispatch<SetStateAction<IFilters>>
  filters: Partial<IFilters>
}

const Filters = ({ refetch, checkedIds, setCheckedIds, setFilters, filters: { label, status } }: IFiltersProps) => {
  const { openModal } = useContext(UiContext);
  const { isLoading, data, isError, refetch: refetchLabels } = useApi('get', '/api/workers/available_labels');
  const [searchQuery, setSearchQuery] = useState<string|null>('');
  const [searching, setSearching] = useState(false);
  const animationRef = useRef(null);

  const changeSearchQuery = useCallback(debounce((searchValue) => {
    setFilters((old) => ({
      ...old,
      label: '',
      page: 1,
      search: searchValue || null,
      status: '',
    }));
  }, 350), []);

  const setSearchHandle = (state: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(state.target.value);
    changeSearchQuery(state.target.value);
  };

  const changeFilter = (state:string, type: keyof IFilters) => {
    setFilters((old) => ({
      ...old!,
      page: 1,
      [type]: ['State', 'Label'].includes(state) ? null : state,
    }));
  };

  return (
    <div className='helpers start'>
      <span className='label'>Filter by</span>
      <SwitchTransition>
        <CSSTransition
          key={searching ? 1 : 0}
          classNames='fade'
          nodeRef={animationRef}
          timeout={0}

        >
          {searching ? (
            <>
              <input
                ref={animationRef}
                type='text'
                className='search-input'
                value={searchQuery ?? ''}
                onChange={setSearchHandle}
              />
              <Icons name='Cancel' className='pane-switcher' onClick={() => {
                setSearching(false);
                setSearchQuery(null);
                setFilters((old) => ({
                  ...old,
                  filters: null,
                  page: 1,
                  search: null,
                }));
              }} />
            </>
          ) : (
            <>
              <div ref={animationRef}>
                <SelectInput
                  value={status ?? 'State'}
                  onChange={(state) => (changeFilter(state as string, 'status'))}
                  options={[
                    {
                      isDefault: true,
                      name: 'State',
                      value: '',
                    },
                    {
                      name: 'Disabled',
                      value: 'dis',
                    },
                    {
                      name: 'Low',
                      value: 'low',
                    },
                    {
                      name: 'Offline',
                      value: 'off',
                    },
                    {
                      name: 'OK',
                      value: 'ok',
                    },

                  ]}
                />
                {data && (
                  <SelectInput
                    value={label!}
                    onChange={(label) => {
                      changeFilter(label as string, 'label');
                    }}
                    options={data?.map((label:string) => ({
                      isDefault: !label,
                      name: label ? label : 'Label',
                      value: label,
                    })) ?? []
                    }
                    disabled={isLoading || isError}
                  />
                )}
              </div>
              <Icons name='Search' className='pane-switcher' onClick={() => {
                setSearching(true);
              }} />
            </>
          )}
        </CSSTransition>
      </SwitchTransition>
      <EditWorkers setting='Setting' refetch={refetchLabels} checkedIds={checkedIds} />
      <EditWorkers setting='Tag' checkedIds={checkedIds} />
      <FlagForRepair checkedIds={checkedIds} setCheckedIds={setCheckedIds} />
      <Icons name='Refresh' onClick={refetch} />
      <button
        className='primary small'
        onClick={() => openModal!('ConnectWorkers')}>Connect Workers</button>
    </div>
  );
};

export default Filters;
